// Tidal Zoning: find NOAA's own archived hydrographic surveys near a project area (linking to
// their official per-survey page, which carries the tide note/zoning chart when one was
// produced), plus a simplified planning-grade tide corrector between two bracketing CO-OPS
// stations for early scoping before a certified tide note exists.
// Background: docs/research/tide-tool.md (task/tide-tool) section 5.
import L from "leaflet";
import { createNoaaCoopsClient } from "../tides/providers/noaa-coops.js";
import { findSurveysInBbox } from "./providers/survey-footprints.js";
import { estimateZoneCorrection } from "./providers/zoning-estimate.js";

const STATE_KEY = "hydrotools:tidal-zoning:v1";

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

function asNum(v, fallback = null) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function fmt(v, digits = 2) {
  return Number.isFinite(v) ? v.toFixed(digits) : "--";
}

function fmtTime(ms) {
  const d = new Date(ms);
  return Number.isFinite(d.getTime()) ? d.toISOString().replace("T", " ").slice(0, 16) + "Z" : "--";
}

function readState() {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const x = raw ? JSON.parse(raw) : {};
    return {
      lat: asNum(x.lat, 37.8063),
      lon: asNum(x.lon, -122.4659),
      tab: x.tab === "estimate" ? "estimate" : "surveys",
      stationAId: typeof x.stationAId === "string" ? x.stationAId : "",
      stationBId: typeof x.stationBId === "string" ? x.stationBId : "",
    };
  } catch {
    return { lat: 37.8063, lon: -122.4659, tab: "surveys", stationAId: "", stationBId: "" };
  }
}

function saveState(state) {
  try { localStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch {}
}

export const tidalZoningTool = {
  id: "tidal-zoning",
  name: "Tidal Zoning",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, containerEl) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(containerEl);
  },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.restore?.(data);
  },
};

function createRuntime(app) {
  const noaa = createNoaaCoopsClient();
  let containerEl = null;
  let state = readState();
  let busy = false;
  let status = "";
  let surveys = [];
  let candidates = []; // nearest stations for the point, to pick A/B from
  let estimate = null;
  let pickMarker = null;
  let mapClickOff = null;

  function persist() { saveState(state); }
  function setStatus(text) { status = String(text || ""); render(); }

  function updateMapMarker() {
    const map = app?.map?.instance;
    if (!map || !Number.isFinite(state.lat) || !Number.isFinite(state.lon)) return;
    if (!pickMarker) {
      pickMarker = L.circleMarker([state.lat, state.lon], { radius: 6, color: "#34d399", weight: 2, fillColor: "#34d399", fillOpacity: 0.25 });
    } else {
      pickMarker.setLatLng([state.lat, state.lon]);
    }
    if (!map.hasLayer(pickMarker)) pickMarker.addTo(map);
    pickMarker.bindTooltip("Project point", { direction: "right", opacity: 0.9 });
  }

  function setMapClickMode(enabled) {
    const map = app?.map?.instance;
    if (!map) return;
    if (mapClickOff) { try { mapClickOff(); } catch {} mapClickOff = null; }
    if (!enabled) return;
    const onClick = (e) => {
      state.lat = Number(e.latlng.lat.toFixed(6));
      state.lon = Number(e.latlng.lng.toFixed(6));
      persist();
      updateMapMarker();
      refreshCandidates();
      render();
    };
    map.on("click", onClick);
    mapClickOff = () => map.off("click", onClick);
  }

  function readForm() {
    if (!containerEl) return;
    const q = (id) => containerEl.querySelector(id);
    state.lat = asNum(q("#tz-lat")?.value, state.lat);
    state.lon = asNum(q("#tz-lon")?.value, state.lon);
    state.stationAId = q("#tz-station-a")?.value || state.stationAId;
    state.stationBId = q("#tz-station-b")?.value || state.stationBId;
    persist();
  }

  async function refreshCandidates() {
    if (!Number.isFinite(state.lat) || !Number.isFinite(state.lon)) return;
    try {
      const hits = await noaa.findNearestStations(state.lat, state.lon, 8);
      candidates = hits;
      if (!state.stationAId && hits[0]) state.stationAId = hits[0].station.id;
      if (!state.stationBId && hits[1]) state.stationBId = hits[1].station.id;
      persist();
      render();
    } catch (err) {
      setStatus(`Station lookup failed: ${err?.message || err}`);
    }
  }

  async function searchSurveys() {
    const map = app?.map?.instance;
    if (!map) { setStatus("Map not available."); return; }
    const b = map.getBounds();
    busy = true;
    setStatus("Searching NOAA's NOS hydrographic survey index for the current map view...");
    try {
      surveys = await findSurveysInBbox(b.getSouth(), b.getWest(), b.getNorth(), b.getEast());
      setStatus(surveys.length ? `${surveys.length} survey(s) found in view.` : "No NOS surveys found in the current map view -- zoom out or pan.");
    } catch (err) {
      surveys = [];
      setStatus(`Survey search failed: ${err?.message || err}`);
    } finally {
      busy = false;
      render();
    }
  }

  async function computeEstimate() {
    readForm();
    if (!state.stationAId || !state.stationBId || state.stationAId === state.stationBId) {
      setStatus("Pick two different control stations.");
      return;
    }
    busy = true;
    setStatus("Fetching hi/lo predictions for both stations...");
    try {
      const end = new Date();
      const start = new Date(end.getTime() - 30 * 86_400_000);
      const [a, b] = await Promise.all([state.stationAId, state.stationBId].map(async (id) => {
        const hit = candidates.find((c) => c.station.id === id)?.station;
        const [{ hilo }, datums] = await Promise.all([
          noaa.fetchSeries({ stationId: id, datum: "MLLW", units: "metric", start: start.toISOString(), end: end.toISOString() }),
          noaa.fetchStationDatums(id).catch(() => ({ datums: [] })),
        ]);
        const mtl = datums.datums?.find((d) => d.name === "MTL")?.value ?? null;
        return { id, lon: hit?.lon, lat: hit?.lat, hilo, mtl, name: hit?.name };
      }));
      estimate = estimateZoneCorrection(a, b, { lon: state.lon, lat: state.lat });
      estimate = estimate && { ...estimate, aName: a.name, bName: b.name };
      setStatus(estimate ? "Planning estimate computed." : "Not enough hi/lo data from one of the stations in the last 30 days.");
    } catch (err) {
      estimate = null;
      setStatus(`Estimate failed: ${err?.message || err}`);
    } finally {
      busy = false;
      render();
    }
  }

  function renderSurveyList() {
    if (!surveys.length) return `<div class="small">No surveys searched yet. Pan/zoom the map to a project area and click Search.</div>`;
    const rows = surveys.slice(0, 60).map((s) => `
      <tr>
        <td>${esc(s.surveyId)}</td>
        <td>${esc(s.year ?? "--")}</td>
        <td>${esc(s.locality || s.sublocality || "--")}</td>
        <td class="small">${esc(s.layer)}</td>
        <td>${s.downloadUrl ? `<a href="${esc(s.downloadUrl)}" target="_blank" rel="noopener">NOAA page</a>` : "--"}</td>
      </tr>
    `).join("");
    return `
      <div class="small" style="margin-bottom:6px;">${surveys.length} survey(s), newest first. "NOAA page" is the survey's own authoritative landing page -- its tide note / zoning chart lives there if one was produced.</div>
      <div style="overflow:auto; max-height:320px;">
        <table style="width:100%; border-collapse:collapse;">
          <thead><tr>
            <th style="text-align:left;">Survey</th><th style="text-align:left;">Year</th>
            <th style="text-align:left;">Locality</th><th style="text-align:left;">Coverage</th><th style="text-align:left;">Link</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    `;
  }

  function stationOptions(selected) {
    return candidates.map((c) => `<option value="${esc(c.station.id)}"${c.station.id === selected ? " selected" : ""}>${esc(c.station.name)} (${esc(c.station.id)}, ${c.distanceKm.toFixed(0)} km)</option>`).join("");
  }

  function renderEstimate() {
    if (!estimate) return `<div class="small">No estimate computed yet.</div>`;
    const hilo = estimate.corrected.slice(-10).map((p) => `
      <tr><td>${esc(p.type === "H" ? "High" : "Low")}</td><td>${esc(fmtTime(p.t))}</td><td style="text-align:right;">${fmt(p.v, 2)} m</td></tr>
    `).join("");
    return `
      <div class="small" style="background:rgba(251,191,36,0.1); border:1px solid rgba(251,191,36,0.35); padding:6px 8px; border-radius:6px; margin-bottom:8px;">
        Planning estimate only -- straight-line interpolation between two stations, not a NOS tide note or certified tidal zoning.
      </div>
      <div class="small">
        Reference station ${esc(estimate.referenceStationId)}, other station ${esc(estimate.otherStationId)} &middot;
        weight toward other ${fmt(estimate.weightTowardOther, 2)}
      </div>
      <div class="small" style="margin-top:4px;">
        Time corrector <strong>${estimate.timeCorrectorHours >= 0 ? "+" : ""}${fmt(estimate.timeCorrectorHours, 2)} h</strong> &middot;
        Range ratio <strong>${fmt(estimate.rangeRatio, 3)}</strong>
        (reference range ${fmt(estimate.rangeReference, 2)} m, other range ${fmt(estimate.rangeOther, 2)} m)
      </div>
      <div class="small" style="margin-top:8px;">Corrected hi/lo at the project point (most recent 10, MLLW-referenced):</div>
      <table style="width:100%; border-collapse:collapse; margin-top:4px;">
        <thead><tr><th style="text-align:left;">Type</th><th style="text-align:left;">Time (UTC)</th><th style="text-align:right;">Level</th></tr></thead>
        <tbody>${hilo}</tbody>
      </table>
    `;
  }

  function render() {
    if (!containerEl) return;
    const tab = state.tab;
    containerEl.innerHTML = `
      <div class="card" style="padding:10px;">
        <div class="lm-title">Tidal Zoning</div>
        <div class="small" style="margin-top:6px;">
          Historical NOS survey finder, plus a simplified planning-grade tide corrector between two control stations.
        </div>

        <div class="row lm-add-row" style="margin-top:10px;">
          <button type="button" class="btn${tab === "surveys" ? " primary" : ""}" data-tz-tab="surveys">Nearby Surveys</button>
          <button type="button" class="btn${tab === "estimate" ? " primary" : ""}" data-tz-tab="estimate">Zone Estimate</button>
        </div>

        ${tab === "surveys" ? `
          <div class="small" style="margin-top:10px;">Pan/zoom the map to a project area, then search NOAA's NOS Hydrographic Surveys index for that view.</div>
          <div class="row lm-add-row" style="margin-top:6px;">
            <button id="tz-search" class="btn primary" ${busy ? "disabled" : ""}>Search current map view</button>
          </div>
          <div id="tz-survey-list" style="margin-top:8px;">${renderSurveyList()}</div>
        ` : `
          <div class="lm-title" style="margin-top:10px;">Project point</div>
          <div class="row lm-add-row">
            <label class="small" style="min-width:48px;">Lat</label>
            <input id="tz-lat" type="number" step="0.0001" value="${esc(state.lat)}" style="flex:1;" />
          </div>
          <div class="row lm-add-row" style="margin-top:6px;">
            <label class="small" style="min-width:48px;">Lon</label>
            <input id="tz-lon" type="number" step="0.0001" value="${esc(state.lon)}" style="flex:1;" />
          </div>
          <div class="row lm-add-row" style="margin-top:6px;">
            <button id="tz-map-center" class="btn">Use Map Center</button>
            <button id="tz-map-pick" class="btn">Pick on Map</button>
            <button id="tz-refresh-stations" class="btn">Find nearby stations</button>
          </div>

          <div class="lm-title" style="margin-top:10px;">Bracketing control stations</div>
          <div class="row lm-add-row">
            <label class="small" style="min-width:16px;">A</label>
            <select id="tz-station-a" style="flex:1;">${stationOptions(state.stationAId)}</select>
          </div>
          <div class="row lm-add-row" style="margin-top:6px;">
            <label class="small" style="min-width:16px;">B</label>
            <select id="tz-station-b" style="flex:1;">${stationOptions(state.stationBId)}</select>
          </div>
          <div class="row lm-add-row" style="margin-top:6px;">
            <button id="tz-compute" class="btn primary" ${busy ? "disabled" : ""}>Compute planning estimate</button>
          </div>
          <div id="tz-estimate" style="margin-top:8px;">${renderEstimate()}</div>
        `}

        <div class="small" style="margin-top:10px; color:var(--muted);">${esc(status)}</div>
      </div>
    `;

    containerEl.querySelectorAll("[data-tz-tab]").forEach((btn) => {
      btn.addEventListener("click", () => { state.tab = btn.dataset.tzTab; persist(); render(); });
    });
    containerEl.querySelector("#tz-search")?.addEventListener("click", searchSurveys);
    containerEl.querySelector("#tz-map-center")?.addEventListener("click", () => {
      const center = app?.map?.instance?.getCenter?.();
      if (!center) return;
      state.lat = Number(center.lat.toFixed(6));
      state.lon = Number(center.lng.toFixed(6));
      persist();
      updateMapMarker();
      refreshCandidates();
      render();
    });
    containerEl.querySelector("#tz-map-pick")?.addEventListener("click", () => setMapClickMode(true));
    containerEl.querySelector("#tz-refresh-stations")?.addEventListener("click", () => { readForm(); refreshCandidates(); });
    containerEl.querySelector("#tz-compute")?.addEventListener("click", computeEstimate);
    ["#tz-lat", "#tz-lon", "#tz-station-a", "#tz-station-b"].forEach((sel) => {
      containerEl.querySelector(sel)?.addEventListener("change", readForm);
    });

    updateMapMarker();
  }

  function mount(el) {
    containerEl = el;
    render();
    refreshCandidates();
  }

  function destroy() {
    if (mapClickOff) { try { mapClickOff(); } catch {} mapClickOff = null; }
    const map = app?.map?.instance;
    if (map && pickMarker && map.hasLayer(pickMarker)) map.removeLayer(pickMarker);
    containerEl = null;
  }

  function snapshot() { return { state }; }
  function restore(data) {
    if (data?.state) state = { ...state, ...data.state };
    render();
  }

  return { mount, destroy, snapshot, restore };
}
